import { FC, useState } from 'react';
import {
    Box, Chip, Divider, Stack, Table, TableBody, TableCell,
    TableHead, TableRow, Typography,
} from '@mui/material';
import { useFeatherHistories, useAiCreditDetail } from '../../../../hooks/useUser';
import { fmtDate, featherReasonLabel } from '../../userShowUtils';
import { PaginatedSection } from '../../userShowComponents';

/** ④-5. 재화 — 깃털 내역(페이징) + AI 크레딧(사용처 집계 + 이력) */
const CurrencyTab: FC<{ userId: number }> = ({ userId }) => {
    const [featherPage, setFeatherPage] = useState(1);
    const { data: featherData, isLoading: featherLoading } = useFeatherHistories(userId, featherPage - 1);
    const featherHistories = featherData?.content ?? [];

    const { data: creditDetail, isLoading: creditLoading } = useAiCreditDetail(userId);
    const usageByReason = creditDetail?.usageByReason ?? [];
    const creditHistory = creditDetail?.history ?? [];

    return (
        <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>깃털 내역</Typography>
            <PaginatedSection
                loading={featherLoading} empty={featherHistories.length === 0} emptyText="깃털 적립/차감 이력이 없습니다."
                page={featherPage} totalPages={featherData?.totalPages ?? 0} onPageChange={setFeatherPage}
            >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>일시</TableCell>
                            <TableCell>내용</TableCell>
                            <TableCell align="right">변동</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {featherHistories.map((h) => (
                            <TableRow key={h.id}>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(h.createdAt)}</TableCell>
                                <TableCell>
                                    <Typography variant="body2">{featherReasonLabel(h.reason)}</Typography>
                                    {h.relatedName && (
                                        <Typography variant="caption" color="text.secondary">{h.relatedName}</Typography>
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    <Typography variant="body2" fontWeight={600} color={h.delta >= 0 ? 'success.main' : 'error.main'}>
                                        {h.delta >= 0 ? '+' : ''}{h.delta.toLocaleString()}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </PaginatedSection>

            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>AI 크레딧</Typography>
            {creditLoading ? (
                <Typography color="text.secondary" sx={{ py: 2 }}>불러오는 중...</Typography>
            ) : (
                <>
                    <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
                        {usageByReason.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">사용 이력이 없습니다.</Typography>
                        ) : (
                            usageByReason.map((u) => (
                                <Chip key={u.reason} label={`${u.reason}: ${u.totalSpent.toLocaleString()}`} size="small" variant="outlined" />
                            ))
                        )}
                    </Stack>
                    {creditHistory.length === 0 ? (
                        <Typography color="text.secondary" sx={{ py: 2 }}>크레딧 변동 이력이 없습니다.</Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>일시</TableCell>
                                    <TableCell>사유</TableCell>
                                    <TableCell align="right">변동</TableCell>
                                    <TableCell align="right">잔액</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {creditHistory.map((h) => (
                                    <TableRow key={h.id}>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(h.createdAt)}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{h.reason}</Typography>
                                            {h.relatedEntity && (
                                                <Typography variant="caption" color="text.secondary">{h.relatedEntity}</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight={600} color={h.delta >= 0 ? 'success.main' : 'error.main'}>
                                                {h.delta >= 0 ? '+' : ''}{h.delta.toLocaleString()}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">{h.balanceAfter.toLocaleString()}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </>
            )}
        </Box>
    );
};

export default CurrencyTab;
